import type { Dispatch, FocusEventHandler, ReactNode, Ref, SetStateAction } from 'react';
import { memo, useCallback, useEffect, useImperativeHandle, useMemo, useState } from 'react';
import type { ErrorOption, FieldErrors } from 'react-hook-form';
import { FormProvider, useForm, useWatch } from 'react-hook-form';
import type { DragEndEvent } from '@dnd-kit/core';
import { closestCenter, DndContext, KeyboardSensor, MouseSensor, TouchSensor, useSensor, useSensors } from '@dnd-kit/core';
import { restrictToVerticalAxis } from '@dnd-kit/modifiers';
import { arrayMove } from '@dnd-kit/sortable';
import type { SvgIconComponent } from '@mui/icons-material';
import { FileDownload } from '@mui/icons-material';
import { Box } from '@mui/material';
import type { ColumnPinningState, PaginationState, TableOptions, VisibilityState, Updater, GroupingState } from '@tanstack/react-table';
import {
  getCoreRowModel,
  getPaginationRowModel,
  getSortedRowModel,
  getExpandedRowModel,
  getGroupedRowModel,
  useReactTable,
} from '@tanstack/react-table';
import { useTranslation } from 'react-i18next';
import {
  updateTableColumnVisibility,
  updateTableColumnPinning,
  updateTableColumnGrouping,
  updateTableColumnAggregation,
  useGetTableQuery,
} from 'store/entities/tablesQuery';
import type { AgendaRecordItem, DataSchema, GetAgendaRecordsRes, GetValueFormItems, MappedFilterValue, Schema } from 'types';
import type { HeliosCellData } from 'types/helios/agendaListing';
import { TableBody } from './TableBody';
import type { TableRow } from './tableFunctions';
import { createEmptyRow, getComponentByType } from './tableFunctions';
import { TableHeader } from './TableHeader';
import { TableFooter } from './TableFooter';
import { useTableEvents } from './useTableEvents';
import { LoadingContainer } from 'components/common/LoadingContainer';
import { useDispatch } from 'react-redux';
import { updateState } from 'store/entities/navigation/actions';
import { ControlButtonsWrapper, NoDataWrapper, StyledTable, TableControl, Wrapper } from './Table.styles';
import { TableLoadingSkeleton } from './TableLoadingSkeleton';
import { Paginator } from './paginator';
import { Button } from 'components/mui/common/Button';
import { isMobileApp } from 'utils/common';
import { formatDate, formatDateWithMask } from 'utils/dateAndTime';
import { isUuid } from 'utils/object';
import type { HeliosRowControls, HeliosRowFunctionsControls, ReturnRow, RowControls, TableAddRowMode } from 'types/table.ts';
import { yupResolver } from '@hookform/resolvers/yup';
import { tableCellValidation } from 'utils/validations/tableCellValidation.ts';
import type { VisibilityResult } from 'types/records.ts';
import { useBeforeUnload } from 'utils/hooks/useBeforeUnload.ts';
import {
  aggregateValues,
  formDataToDataD,
  getAggregationConfig,
  getAggregationFormValue,
  getCellGroupingValue,
  getSelectRowId,
} from 'utils/table.ts';
import { processKeys } from 'utils/agendaRecord/record.ts';
import type { ContextMenuItem } from 'utils/hooks/userAgendaTable/useAgendaContextMenu.ts';
import { getFormFieldName } from './editableComponentFactory.tsx';
import { TableManagementProvider } from 'contexts/TableManagementContext.tsx';
import { useTableFormSync } from 'utils/hooks/useTableFormSync.ts';
import { useFormSnapshot } from 'utils/useFormSnapshot.tsx';
import type { FormStateSnapshot, Snapshot } from 'contexts/FormRegistryProvider.tsx';
import { TABLE_CELL_TYPE, VIEW_TYPE } from 'types/enums.ts';
import { CatalogueWidget } from '../../user/catalogueWidget/CatalogueWidget.tsx';
import type { ViewParentType } from 'types/views.ts';
import { MobileTable } from './mobile/MobileTable.tsx';
import { MobileHeliosTable } from './mobile/MobileHeliosTable.tsx';
import { MobileSelectionCountFab } from './mobile/components/MobileSelectionCountFab.tsx';
import { ColumnVisibilityController } from './actions/ColumnVisibilityController.tsx';
import { ColumnGroupingControler } from './actions/ColumnGroupingControler.tsx';
import { useTableHeight } from './useTableHeight.ts';
import { FeatureGate } from '../Posthog/FeatureGate.tsx';
import { useLocation } from 'react-router-dom';
import type { HeliosRecordDataTableColumnSettings } from 'types/dataHelios.ts';
import type { TableAggregationConfig, TableAggregationType, TableColumnAggregationState } from 'utils/table.ts';
import { downloadCsv } from 'utils/file/downloadFile.ts';
import { useColumnSizing } from 'utils/hooks/useColumnSizing';

export type TableFormDataRow<T = unknown> = Record<string, T>;
export type TableFormData<T = TableFormDataRow> = Record<string, T>;
export type DeepPartial<T> = T extends readonly (infer U)[]
  ? readonly DeepPartial<U>[]
  : T extends (infer U)[]
    ? DeepPartial<U>[]
    : T extends object
      ? { [P in keyof T]?: DeepPartial<T[P]> }
      : T;
export type TableDirtyFields = DeepPartial<TableFormData<TableFormDataRow<boolean>>>;

export interface TableRef {
  triggerForm: () => Promise<boolean>;
  getValues: () => TableFormData;
  exportToCSV: () => void;
  getFormState: () => {
    isDirty: boolean;
    dirtyFields: TableDirtyFields;
    isValid: boolean;
    errors: FieldErrors;
  };
  updateFormDataRow: (rowId: string, data: TableFormDataRow) => void;
  resetSavedForm: () => void;
  resetField: (path: string, value: TableFormDataRow) => void;
  clearFormState: () => void;
  buildSnapshot: () => FormStateSnapshot<DataSchema>;
  setError: (
    name: string,
    error: ErrorOption,
    options?: {
      shouldFocus: boolean;
    }
  ) => void;
  clearSelection: () => void;
}

export interface TableProps<T extends keyof P, P extends DataSchema> {
  tableRef?: Ref<TableRef>;
  initialSnapshot?: Snapshot<T, P>;
  data: TableOptions<Schema<T, P>>['data'];
  dataSchema: P extends DataSchema
    ? P
    : never & {
        identifier: string;
      };
  forceResetFormState?: boolean;
  onFormStateReset?: () => void;

  rows?: number;
  pageSize?: number | null;
  variant?: 'auto' | 'fixed';
  page?: number;

  draggable?: boolean;

  onSelectRow?: (rowIds: string[], rows?: TableOptions<Schema<T, P>>['data']) => void;
  onRowsOrderChanged?: (rows: TableOptions<Schema<T, P>>['data']) => void;

  onCurrentPageChange?: (currentPage: number) => void;
  onPageSizeChange?: (pageSize: number) => void;

  sort?: {
    column: keyof P;
    direction: 'asc' | 'desc';
  };
  onSort?: (sort: { column: keyof P; direction: 'asc' | 'desc' }) => void;

  onRowClick?: (row: TableOptions<Schema<T, P>>['data']) => unknown;
  onCellClick?: (row: TableOptions<Schema<T, P>>['data'][number], cell: keyof P, newRow?: TableOptions<Schema<T, P>>['data'][number]) => unknown;
  onEntireRowClick?: (row: TableOptions<Schema<T, P>>['data'][number], tableId: string) => unknown;
  showRowClickStyles?: boolean;

  controls?: ReactNode;
  isEnableExportToCSV?: boolean;
  isEnableColumnHiding?: boolean;
  isEnableColumnGrouping?: boolean;
  isEnableColumnAggregation?: boolean;
  clickableCellsKeys?: (keyof P)[] | 'all';
  unableSortFor?: (keyof P)[] | 'none' | 'all';

  onSaveEditedRow?: ReturnRow<P>;
  onCancelEditRow?: (id: string) => void;
  editRowId?: string[] | null;
  idsForMobileInRowEdit?: string[] | null;

  addRowMode?: TableAddRowMode;
  addedRow?: boolean;
  addedRows?: TableRow<T, P>[];
  innerTable?: (row: TableOptions<Schema<T, P>>['data'][number], tableIndex: number) => ReactNode;
  hideHeader?: boolean;
  hidePagination?: boolean;
  hideCheckbox?: boolean;
  isMultiSelect?: boolean;
  visibleRowCount?: number;

  tableIndex?: number;
  onAddButtonClick?: () => void;

  tableControls?: {
    [key: string]: {
      visible: boolean;
      disabled?: boolean;
      onClick: () => void;
      icon: SvgIconComponent;
      label: string;
      id: string;
    };
  };

  rowControls?: RowControls<TableOptions<Schema<T, P>>['data'][number]>;
  heliosRowControls?: HeliosRowControls<TableOptions<Schema<T, P>>['data'][number]>;
  heliosRowFunctionsControls?: HeliosRowFunctionsControls[];
  isLoading?: boolean;
  defaultSelectedRows?: string[];
  persistHistory?: boolean;
  tableId?: string;
  viewId?: string;
  agendaId?: string;
  isHeliosTable?: boolean;
  isEditableColumnsAllowed?: boolean;
  onBlurCapture?: FocusEventHandler<HTMLTableSectionElement>;
  visibilityResults?: Record<string, VisibilityResult>;
  displayButtons?: boolean;
  contextMenuItems?: ContextMenuItem[];
  isPinnedDashboard?: boolean;
  disableRowCancelEdit?: boolean;
  disableRowSave?: boolean;
  prePaginationChange?: () => Promise<{ action: 'proceed' | 'cancel' | 'close' }>;
  paginationZIndex?: number;
  viewType?: VIEW_TYPE;
  viewWidgetId?: string | null;
  parentId?: string;
  viewParentType?: ViewParentType;
  isModal?: boolean;
  setCatalogData?: Dispatch<SetStateAction<GetAgendaRecordsRes<AgendaRecordItem[]>>>;
  isOverview?: boolean;
  noData?: React.ReactNode;
  disablePageSizeChange?: boolean;
  showAllRows?: boolean;
  defaultColumnSize?: number;
  fixedHeight?: string;
  isColumnSizingResetEnabled?: boolean;
  dataTestIdPrefix?: string;
  testIdPrefix?: string;
}

export interface TablePersistentState extends Record<string, unknown> {
  pagination: {
    page: number;
    pageSize: number;
  };
  lastClickedRow?: {
    id: string;
    absoluteIndex: number;
    relativeIndex: number;
    [key: string]: unknown;
  };
  queryData?: {
    params: {
      agendaId: string;
      viewId: string;
      page: number;
      pageSize: number;
      search: string;
      orderBy: string;
      formItemId: string;
      orderDesc: string;
    };
    skipViewConditions?: boolean;
    recordIds?: string[];
    valueFormItems?: GetValueFormItems[];
    filters?: MappedFilterValue[];
  };
  other: {
    viewId: string;
    agendaId: string;
    tabId?: string;
  };
  timestamp: number;
}

const DEFAULT_ADDED_ROWS = [] as never[];
const DEFAULT_CLICKABLE_CELLS_KEYS = [] as never[];

const TableBase = <T extends keyof P, P extends DataSchema>({
  tableRef,
  initialSnapshot,
  data,
  dataSchema,
  forceResetFormState,
  onFormStateReset,
  onSelectRow,
  rows = 1,
  pageSize,
  draggable,
  onCurrentPageChange,
  onPageSizeChange,
  sort,
  onSort,
  controls,
  isEnableExportToCSV = false,
  isEnableColumnHiding = true,
  isEnableColumnGrouping = true,
  isEnableColumnAggregation = true,
  onRowsOrderChanged,
  onCellClick,
  onEntireRowClick,
  showRowClickStyles = true,
  page = 1,
  clickableCellsKeys = DEFAULT_CLICKABLE_CELLS_KEYS,
  unableSortFor = 'none',
  onSaveEditedRow,
  onCancelEditRow,
  editRowId,
  idsForMobileInRowEdit,
  addRowMode = 'single',
  addedRow,
  addedRows = DEFAULT_ADDED_ROWS,
  innerTable,
  hideHeader,
  hidePagination,
  tableIndex = 0,
  onAddButtonClick,
  rowControls,
  heliosRowControls,
  heliosRowFunctionsControls,
  tableControls,
  hideCheckbox,
  visibleRowCount,
  isMultiSelect = true,
  isLoading,
  defaultSelectedRows,
  persistHistory = false,
  tableId = '',
  viewId = '',
  agendaId = '',
  isHeliosTable = false,
  isEditableColumnsAllowed,
  onBlurCapture,
  visibilityResults,
  displayButtons,
  contextMenuItems,
  isPinnedDashboard,
  disableRowCancelEdit,
  disableRowSave,
  prePaginationChange,
  paginationZIndex,
  viewType,
  viewWidgetId,
  isModal,
  setCatalogData,
  isOverview = false,
  noData,
  disablePageSizeChange,
  dataTestIdPrefix,
  showAllRows,
  defaultColumnSize = 150,
  fixedHeight,
  isColumnSizingResetEnabled = false,
  testIdPrefix,
}: TableProps<T, P>) => {
  const resolvedTestIdPrefix = dataTestIdPrefix ?? testIdPrefix;
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const mobileApp = isMobileApp();
  const { pathname } = useLocation();
  /**
   * The unique identifier for all tables is the generated uuid path
   */
  const queryKey = pathname || 'default-table';
  const tableQueryState = useGetTableQuery(queryKey);

  const [dataD, setDataD] = useState<Schema<T, P>[]>(data);
  const [tableSelectedRows, setSelectedRows] = useState<string[]>(defaultSelectedRows || []);
  const [columnVisibility, setColumnVisibility] = useState<VisibilityState>(tableQueryState?.columnVisibility || {});
  const [columnPinning, setColumnPinning] = useState<ColumnPinningState>(tableQueryState?.columnPinning || { left: [], right: [] });
  const [columnGrouping, setColumnGrouping] = useState<GroupingState>(tableQueryState?.columnGrouping || []);
  const [columnAggregation, setColumnAggregation] = useState<TableColumnAggregationState>(tableQueryState?.columnAggregation || {});

  const handleColumnVisibilityChange = useCallback(
    (updaterOrValue: Updater<VisibilityState>) => {
      setColumnVisibility((prev) => {
        const newVal = typeof updaterOrValue === 'function' ? updaterOrValue(prev) : updaterOrValue;
        dispatch(updateTableColumnVisibility({ key: queryKey, columnVisibility: newVal }));
        return newVal;
      });
    },
    [dispatch, queryKey]
  );

  const { columnSizing, handleColumnSizingChange, handleColumnSizingReset, handleAllColumnSizingReset, hasChangedColumnSizing } = useColumnSizing({
    tableId,
    agendaId,
    viewId,
    queryKey,
    defaultColumnSize,
  });

  const handleColumnPinningChange = useCallback(
    (updaterOrValue: Updater<ColumnPinningState>) => {
      setColumnPinning((prev) => {
        const newVal = typeof updaterOrValue === 'function' ? updaterOrValue(prev) : updaterOrValue;
        dispatch(updateTableColumnPinning({ key: queryKey, columnPinning: newVal }));
        return newVal;
      });
    },
    [dispatch, queryKey]
  );

  const handleColumnGroupingChange = useCallback(
    (updaterOrValue: Updater<GroupingState>) => {
      setColumnGrouping((prev) => {
        const newVal = typeof updaterOrValue === 'function' ? updaterOrValue(prev) : updaterOrValue;
        dispatch(updateTableColumnGrouping({ key: queryKey, columnGrouping: newVal }));
        return newVal;
      });
    },
    [dispatch, queryKey]
  );

  const handleColumnAggregationChange = useCallback(
    (columnId: string, aggregation: TableAggregationType | null) => {
      setColumnAggregation((prev) => {
        const newVal = {
          ...prev,
          [columnId]: aggregation,
        };
        dispatch(updateTableColumnAggregation({ key: queryKey, columnAggregation: newVal }));
        return newVal;
      });

      setColumnGrouping((prev) => {
        if (!prev.length) return prev;

        const nextGrouping = [...prev];
        dispatch(updateTableColumnGrouping({ key: queryKey, columnGrouping: nextGrouping }));
        return nextGrouping;
      });
    },
    [dispatch, queryKey]
  );

  const [paginator, setPaginator] = useState<PaginationState>({
    pageIndex: page - 1,
    pageSize: pageSize ?? 10,
  });

  const totalComponentsHeight = useTableHeight();
  const tableHeight = fixedHeight ?? (isPinnedDashboard ? '100%' : `calc(100vh - ${totalComponentsHeight}px)`);

  useEffect(() => {
    if (defaultSelectedRows) {
      setSelectedRows(defaultSelectedRows);
    }
  }, [defaultSelectedRows]);

  const visibleDataD = useMemo(() => (visibleRowCount ? dataD.slice(0, visibleRowCount) : dataD), [dataD, visibleRowCount]);

  const validationSchema = useMemo(() => tableCellValidation(dataSchema, visibleDataD), [dataSchema, visibleDataD]);

  const form = useForm<TableFormData>({
    resolver: yupResolver(validationSchema),
    mode: 'onBlur',
  });

  const { getValues, setValue, reset, formState, trigger, resetField, setError } = form;
  const formValues = useWatch({ control: form.control });

  const { buildSnapshot } = useFormSnapshot(form, dataSchema);

  const selectAllForm = useForm<TableFormData<boolean>>({
    defaultValues: {
      selectAll: false,
    },
  });

  const { control: selectAllControl, setValue: selectAllSetValue } = selectAllForm;

  useBeforeUnload({
    when: formState.isDirty,
  });

  const isSelectedAll = useWatch({ control: selectAllControl, name: 'selectAll' });

  const schemaKeys = Object.keys(dataSchema);

  const resolvedColumnAggregation = useMemo<TableColumnAggregationState>(
    () => Object.fromEntries(schemaKeys.map((key) => [String(key), columnAggregation[String(key)] ?? null])),
    [columnAggregation, schemaKeys]
  );

  const getAggregatedValue = useCallback(
    (
      columnKey: keyof P,
      aggregationConfig: TableAggregationConfig<Schema<T, P>>,
      rowsToAggregate: Schema<T, P>[],
      columnType?: TABLE_CELL_TYPE,
      settings?: HeliosRecordDataTableColumnSettings
    ) => {
      return aggregateValues(
        rowsToAggregate.map((row) => {
          const rowFormValues = formValues?.[row.identifier];
          const rowForAggregation = rowFormValues
            ? ({
                ...row,
                ...Object.fromEntries(
                  Object.entries(rowFormValues).map(([key, value]) => [
                    key,
                    getAggregationFormValue((row as Record<string, unknown>)[key], value, dataSchema[key as keyof P]?.type, aggregationConfig.type),
                  ])
                ),
              } as Schema<T, P>)
            : row;
          const rawValue = aggregationConfig.getValue?.(rowForAggregation) ?? rowForAggregation[columnKey as keyof Schema<T, P>];

          return rawValue;
        }),
        aggregationConfig.type,
        columnType,
        settings
      );
    },
    [dataSchema, formValues]
  );

  const renderAggregatedValue = useCallback(
    (
      columnKey: keyof P,
      aggregationConfig: TableAggregationConfig<Schema<T, P>>,
      value: unknown,
      rowsToAggregate: Schema<T, P>[],
      columnType?: TABLE_CELL_TYPE,
      dateMask?: string
    ) => {
      if (value === null || value === undefined) return null;

      if (aggregationConfig.render) {
        return aggregationConfig.render({
          columnId: String(columnKey),
          rows: rowsToAggregate,
          type: aggregationConfig.type,
          value,
        });
      }

      if (value instanceof Date) {
        if (columnType === TABLE_CELL_TYPE.DATE && dateMask) {
          return formatDateWithMask(value, dateMask);
        }

        return formatDate(value, columnType === TABLE_CELL_TYPE.DATE ? 'dd.MM.yyyy' : 'dd.MM.yyyy HH:mm');
      }

      return String(value);
    },
    []
  );

  const columns: TableOptions<Schema<T, P>>['columns'] = useMemo(() => {
    const filteredKeys = schemaKeys.filter((key) => {
      const isButtonKey = dataSchema[key].formItemDefinition?.type === 'Button';

      return displayButtons ? !dataSchema[key].hidden : !dataSchema[key].hidden && !isButtonKey;
    });

    const hasOrderDefined = filteredKeys.some((key) => dataSchema[key].order !== undefined);

    const sortedKeys = hasOrderDefined
      ? filteredKeys.sort((a, b) => {
          const orderA = dataSchema[a].order ?? Number.MAX_SAFE_INTEGER;
          const orderB = dataSchema[b].order ?? Number.MAX_SAFE_INTEGER;
          return orderA - orderB;
        })
      : filteredKeys;

    const { keys, schema } = processKeys({ sortedKeys, dataSchema, isNested: displayButtons });

    return keys.map((key) => {
      const item = schema?.[key];
      const aggregationConfig = isEnableColumnAggregation ? getAggregationConfig(columnAggregation[String(key)]) : undefined;
      const dateMask = item?.formItemDefinition?.date?.dateMask;

      return {
        header: item?.customKey || (item?.translationKey ? t(item.translationKey) : ''),
        id: item?.label,
        accessorKey: key,
        size: item?.size,
        getGroupingValue: (row) => getCellGroupingValue(item?.type ?? TABLE_CELL_TYPE.TEXT, row[key as keyof Schema<T, P>]),
        meta: {
          accessorKey: String(key),
          processedSchema: schema,
          /**
           * Cell styles that apply to the entire column, if concrete cell style is missing.
           * Not used for now, only styling of concrete cells is currently used.
           */
          uiDefinition: undefined,
        },
        aggregationFn:
          aggregationConfig && aggregationConfig.includeInGrouping !== false
            ? (_columnId, leafRows) =>
                getAggregatedValue(
                  key as keyof P,
                  aggregationConfig,
                  leafRows.map((row) => row.original),
                  item?.type,
                  item?.settings
                )
            : undefined,
        aggregatedCell:
          aggregationConfig && aggregationConfig.includeInGrouping !== false
            ? (cell) =>
                renderAggregatedValue(
                  key as keyof P,
                  aggregationConfig,
                  cell.getValue(),
                  cell.row.subRows.length ? cell.row.subRows.map((row) => row.original) : [cell.row.original],
                  item?.type,
                  dateMask
                )
            : undefined,
        footer:
          aggregationConfig && aggregationConfig.includeInFooter !== false
            ? () =>
                renderAggregatedValue(
                  key as keyof P,
                  aggregationConfig,
                  getAggregatedValue(key as keyof P, aggregationConfig, dataD, item?.type, item?.settings),
                  dataD,
                  item?.type,
                  dateMask
                )
            : undefined,

        cell: (row) => {
          const heliosCellData = isHeliosTable ? (row.row.original[key as keyof Schema<T, P>] as unknown as HeliosCellData) : undefined;
          const cellKey = getFormFieldName(row.row, item.label);
          const formItemUiDefinition = isHeliosTable
            ? (heliosCellData?.uiDefinition ?? undefined)
            : (visibilityResults?.[cellKey]?.formItemUiDefinition ?? undefined);

          return getComponentByType<Schema<T, P>, P>(
            item?.type,
            item?.originalType,
            row.getValue(),
            row,
            onCellClick,
            clickableCellsKeys,
            item?.values as string[] | undefined,
            item?.dataType,
            item?.settings,
            item?.formatting,
            item?.templateItemId,
            item?.templateViewId,
            heliosCellData,
            formItemUiDefinition,
            displayButtons,
            contextMenuItems
          );
        },
      };
    });
  }, [
    clickableCellsKeys,
    columnAggregation,
    contextMenuItems,
    dataD,
    dataSchema,
    displayButtons,
    getAggregatedValue,
    isEnableColumnAggregation,
    isHeliosTable,
    onCellClick,
    renderAggregatedValue,
    schemaKeys,
    t,
    visibilityResults,
  ]);

  const tableInstance = useReactTable({
    columns,
    data: dataD,
    getCoreRowModel: getCoreRowModel(),
    getPaginationRowModel: getPaginationRowModel(),
    getSortedRowModel: getSortedRowModel(),
    getGroupedRowModel: getGroupedRowModel(),
    getExpandedRowModel: getExpandedRowModel(),
    manualPagination: true,
    rowCount: rows,
    autoResetPageIndex: true,
    columnResizeMode: 'onEnd',
    defaultColumn: {
      size: defaultColumnSize,
      minSize: 50,
    },
    state: {
      columnPinning,
      columnVisibility,
      columnSizing,
      grouping: columnGrouping,
    },
    onColumnVisibilityChange: handleColumnVisibilityChange,
    onColumnPinningChange: handleColumnPinningChange,
    onColumnSizingChange: handleColumnSizingChange,
    onGroupingChange: handleColumnGroupingChange,
  });

  const hasAggregationFooter = tableInstance
    .getFooterGroups()
    .some((footerGroup) => footerGroup.headers.some((header) => header.column.getIsVisible() && Boolean(header.column.columnDef.footer)));

  /**
   * Keep committed sizing unchanged during resize. The active drag is applied
   * only to CSS variables until TanStack commits it on resize end.
   */
  const { columnSizingInfo } = tableInstance.getState();
  const columnSizeVars = useMemo(() => {
    const headers = tableInstance.getFlatHeaders();
    const COL_META_SIZE = 40;
    const COL_SELECTION_SIZE = 40;

    const colSizes: Record<string, string> = {
      '--col-selection-size': `${COL_SELECTION_SIZE}`,
      '--col-meta-size': `${COL_META_SIZE}`,
      '--col-actions-size': '100',
    };

    let totalDataWidth = 0;
    for (let i = 0; i < headers.length; i++) {
      const header = headers[i]!;
      const baseColumnSize = columnSizing[header.column.id] ?? header.column.columnDef.size ?? defaultColumnSize;
      const isResizingColumn = columnSizingInfo.isResizingColumn === header.column.id;
      const resizingColumnSize = isResizingColumn
        ? (columnSizingInfo.startSize ?? baseColumnSize) + (columnSizingInfo.deltaOffset ?? 0)
        : baseColumnSize;
      const minColumnSize = header.column.columnDef.minSize ?? 50;
      const maxColumnSize = header.column.columnDef.maxSize ?? Number.MAX_SAFE_INTEGER;
      const columnSize = Math.min(maxColumnSize, Math.max(minColumnSize, resizingColumnSize));
      colSizes[`--header-${header.id}-size`] = `${columnSize}`;
      colSizes[`--col-${header.column.id}-size`] = `${columnSize}`;
      if (header.id !== 'id' && header.id !== 'hidden' && columnVisibility[header.column.id] !== false && header.column.getIsVisible()) {
        totalDataWidth += columnSize;
      }
    }

    const selectionWidth = hideCheckbox ? 0 : COL_SELECTION_SIZE;
    const innerTableWidth = innerTable ? COL_META_SIZE : 0;
    const draggableWidth = draggable ? COL_META_SIZE : 0;

    const numberOfVisibleRowControls = Object.values(rowControls ?? {}).filter((c) => c.visible).length;
    const numberOfVisibleTableControls = Object.values(tableControls ?? {}).filter((c) => c.visible).length;
    const hasVisibleHeliosRowControls = heliosRowControls ? Object.keys(heliosRowControls).length > 0 : false;
    const shouldShowRowControls = numberOfVisibleRowControls > 0 || numberOfVisibleTableControls > 0 || hasVisibleHeliosRowControls;
    const hasHeliosFunctions = (heliosRowFunctionsControls ?? []).length > 0;
    const metaColumnsCount = Number(shouldShowRowControls) + Number(hasHeliosFunctions);
    const effectiveMetaColumnsCount = metaColumnsCount + Number(shouldShowRowControls);

    colSizes['--total-data-width'] =
      `${totalDataWidth + selectionWidth + innerTableWidth + draggableWidth + COL_META_SIZE * effectiveMetaColumnsCount}`;
    return colSizes;
  }, [
    columnSizing,
    columnSizingInfo,
    columnVisibility,
    defaultColumnSize,
    draggable,
    heliosRowControls,
    heliosRowFunctionsControls,
    hideCheckbox,
    innerTable,
    rowControls,
    tableControls,
    tableInstance,
  ]);

  useEffect(() => {
    setDataD(() => {
      let newDataD = [...data];

      if (addRowMode === 'single') {
        const currentHasNewRow = newDataD.some((row) => row.identifier?.startsWith('new'));

        if (addedRow && !currentHasNewRow) {
          const newRow = createEmptyRow<T, P>(dataSchema);
          newDataD = [newRow, ...newDataD];
        } else if (!addedRow && currentHasNewRow) {
          newDataD = newDataD.filter((row) => !row.identifier?.startsWith('new'));
        }
      } else {
        const addedRowIds = new Set(addedRows.map((row) => row.identifier));

        newDataD = newDataD.filter((row) => {
          return !(row.identifier?.startsWith('new') && !addedRowIds.has(row.identifier));
        });

        const currentDataDIds = new Set(newDataD.map((row) => row.identifier));
        const rowsToAdd = addedRows.filter((row) => !currentDataDIds.has(row.identifier));

        if (rowsToAdd.length > 0) {
          newDataD = [...newDataD];
          rowsToAdd.forEach((row) => {
            const index = newDataD.findIndex((r) => r.id === (row as { originalRowId?: string }).originalRowId);
            if (index === -1) {
              newDataD.unshift(row);
              return;
            }
            newDataD.splice(index + 1, 0, row);
          });
        }
      }

      return newDataD;
    });
  }, [data, addedRow, addedRows, addRowMode, dataSchema]);

  useTableFormSync<T, P>({
    data: visibleDataD,
    initialSnapshot,
    dataSchema,
    forceReset: forceResetFormState,
    getValues,
    reset,
    setValue,
    setError,
    onFormStateReset,
    paginator,
  });

  const sensors = useSensors(useSensor(MouseSensor, {}), useSensor(TouchSensor, {}), useSensor(KeyboardSensor, {}));

  useTableEvents<T, P>({
    pagination: paginator,
    tableInstance,
    actions: {
      getCurrentPage: onCurrentPageChange,
      getPageSize: onPageSizeChange,
    },
  });

  const handleExportToCSV = useCallback(() => {
    const _headers = tableInstance.getFlatHeaders().map((header) => `"${header.column.columnDef.header}"`);
    const _rows = tableInstance.getRowModel().rows.map((row) =>
      row
        .getAllCells()
        .map((cell) => {
          const value = cell.getValue();

          if (Array.isArray(value)) {
            const hasFullName = value?.some((val) => {
              if (val !== null && typeof val === 'object') {
                return Object.hasOwn(val, 'fullName');
              }
            });
            const hasComment = value?.some((val) => {
              if (val !== null && typeof val === 'object') {
                return Object.hasOwn(val, 'text');
              }
            });
            const hasIdAndValue = value?.some((val) => {
              if (val !== null && typeof val === 'object') {
                return Object.hasOwn(val, 'value') && Object.hasOwn(val, 'value');
              }
            });
            const hasDate = value?.some((val) => typeof val !== 'number' && !isNaN?.(Date?.parse(val)));
            const hasUuid = value?.some((val) => isNaN?.(val) && isUuid(val));
            const hasNumber = value?.some((val) => typeof val === 'number' && !isNaN(val));

            if (hasUuid || hasComment) {
              return `"${value.length}"`;
            }

            if (hasFullName) {
              return `"${value.map((val) => val.fullName).join(', ')}"`;
            }

            if (hasIdAndValue) {
              return `"${value.map((val) => val.value).join(', ')}"`;
            }

            if (hasDate) {
              return `"${formatDate(new Date(value[0]), 'dd.MM.yyyy HH:mm')}"`;
            }

            if (hasNumber) {
              return `"${value}"`;
            }

            if (value) {
              return `"${value.join(', ')}"`;
            }
          }

          return `"${value ?? ''}"`;
        })
        .join(',')
    );

    const csvContent = [_headers.join(','), ..._rows].join('\n');

    downloadCsv(csvContent, `table-export-${formatDate(new Date(), 'yyyy-MM-dd-HH-mm-ss')}`);
  }, [tableInstance]);

  const handleRowSelect = useCallback(
    (rowId: string, isChecked: boolean) => {
      let updatedSelectedRows: string[];

      if (isMultiSelect) {
        updatedSelectedRows = isChecked ? [...tableSelectedRows, rowId] : tableSelectedRows.filter((id) => id !== rowId);
      } else {
        updatedSelectedRows = isChecked ? [rowId] : [];
      }

      setSelectedRows(updatedSelectedRows);
      onSelectRow?.(
        updatedSelectedRows,
        data.filter((row) => updatedSelectedRows.includes(getSelectRowId(row, isHeliosTable)))
      );
    },
    [data, isHeliosTable, isMultiSelect, onSelectRow, tableSelectedRows]
  );

  const handleToggleSelectAll = useCallback(() => {
    if (isSelectedAll) {
      const filteredRowsIds = tableSelectedRows.filter((selectedRowId) => !data.some((row) => getSelectRowId(row, isHeliosTable) === selectedRowId));

      onSelectRow?.(
        filteredRowsIds,
        data.filter((row) => filteredRowsIds.includes(getSelectRowId(row, isHeliosTable)))
      );

      setSelectedRows(filteredRowsIds);
      selectAllSetValue('selectAll', false);
    } else {
      selectAllSetValue('selectAll', true);
      setSelectedRows((prevState) => {
        const newSelection = [...new Set([...prevState, ...data.map((row) => getSelectRowId(row, isHeliosTable))])];

        onSelectRow?.(
          newSelection,
          data.filter((row) => newSelection.includes(getSelectRowId(row, isHeliosTable)))
        );

        return newSelection;
      });
    }
  }, [data, isHeliosTable, isSelectedAll, onSelectRow, selectAllSetValue, tableSelectedRows]);

  const handleDragEnd = useCallback(
    (event: DragEndEvent) => {
      const { active, over } = event;
      if (active && over && active.id !== over.id) {
        setDataD((value) => {
          const oldIndex = value.findIndex((row) => row.identifier === active.id);
          const newIndex = value.findIndex((row) => row.identifier === over.id);

          const newValue = arrayMove(value, oldIndex, newIndex);
          if (onRowsOrderChanged) onRowsOrderChanged(newValue);

          return newValue;
        });
      }
    },
    [onRowsOrderChanged]
  );

  const calculateRowIndex = useCallback(
    (relativeIndex: number): number => {
      const currentPage = paginator.pageIndex + 1;
      // eslint-disable-next-line @typescript-eslint/no-shadow
      const pageSize = paginator.pageSize;
      return (currentPage - 1) * pageSize + relativeIndex;
    },
    [paginator.pageIndex, paginator.pageSize]
  );

  // eslint-disable-next-line @typescript-eslint/no-shadow
  const calculateRelativeIndex = useCallback((absoluteIndex: number, pageSize: number): number => {
    return absoluteIndex % pageSize;
  }, []);

  const handleEntireRowClick = useCallback(
    (row: TableOptions<Schema<T, P>>['data'][number]) => {
      const rowKey = row.identifier ?? row.id;
      const absoluteIndex = calculateRowIndex(data.findIndex((r) => (r.identifier ?? r.id) === rowKey));
      const otherSection = viewId
        ? {
            other: {
              viewId,
              agendaId,
              tabId: viewId,
            },
          }
        : {};
      dispatch(
        updateState({
          key: `table-${tableId}`,
          data: {
            lastClickedRow: {
              id: rowKey,
              absoluteIndex: absoluteIndex,
              relativeIndex: calculateRelativeIndex(absoluteIndex, paginator.pageSize),
            },
            ...otherSection,
          },
        })
      );
      onEntireRowClick?.(row, tableId);
    },
    [agendaId, calculateRelativeIndex, calculateRowIndex, data, dispatch, onEntireRowClick, paginator.pageSize, tableId, viewId]
  );

  const handlePaginatorChange = useCallback(
    async (value: PaginationState | ((prev: PaginationState) => PaginationState)) => {
      const newState = typeof value === 'function' ? value(paginator) : value;

      if (prePaginationChange) {
        const { action } = (await prePaginationChange?.()) || {};

        if (action === 'cancel') {
          return;
        }
      }

      if (newState.pageIndex !== paginator.pageIndex) {
        onCurrentPageChange?.(newState.pageIndex + 1);
      }
      if (newState.pageSize !== paginator.pageSize) {
        onPageSizeChange?.(newState.pageSize);
      }

      if (persistHistory) {
        dispatch(
          updateState({
            key: `table-${tableId}`,
            data: {
              other: {
                viewId,
              },
              pagination: {
                page: newState.pageIndex + 1,
                pageSize: newState.pageSize,
              },
              timestamp: Date.now(),
            },
          })
        );
      }

      setPaginator(newState);
      return newState;
    },
    [paginator, prePaginationChange, persistHistory, onCurrentPageChange, onPageSizeChange, dispatch, tableId, viewId]
  );

  useImperativeHandle(
    tableRef,
    (): TableRef => ({
      setError: (path, error, options?) => setError(path, error, options),
      triggerForm: () => trigger(),
      getValues: () => getValues(),
      exportToCSV: handleExportToCSV,
      getFormState: () => ({
        isDirty: formState.isDirty,
        dirtyFields: formState.dirtyFields,
        isValid: formState.isValid,
        errors: formState.errors,
      }),
      updateFormDataRow: (rowId: string, rowData: TableFormDataRow) => {
        const currentFormData = getValues();
        const updatedFormData = {
          ...currentFormData,
          [rowId]: { ...rowData },
        };

        reset(updatedFormData, { keepDirty: true });
        setDataD(formDataToDataD(updatedFormData));
      },
      resetSavedForm: () => {
        reset(getValues(), { keepDirty: false });
      },
      resetField: (path: string, value: TableFormDataRow) => {
        resetField(path, {
          defaultValue: value,
          keepDirty: false,
        });
      },
      clearFormState: () => {
        reset({}, { keepDirty: false, keepDirtyValues: false });
      },
      buildSnapshot,
      clearSelection: () => {
        setSelectedRows([]);
        selectAllSetValue('selectAll', false);
        onSelectRow?.([], []);
      },
    }),
    [
      buildSnapshot,
      setError,
      trigger,
      getValues,
      handleExportToCSV,
      formState.isDirty,
      formState.dirtyFields,
      formState.isValid,
      formState.errors,
      reset,
      resetField,
      setSelectedRows,
      selectAllSetValue,
      onSelectRow,
    ]
  );

  useEffect(() => {
    if (tableQueryState?.columnVisibility) {
      setColumnVisibility(tableQueryState.columnVisibility);
    }
  }, [tableQueryState?.columnVisibility]);

  useEffect(() => {
    if (tableQueryState?.columnPinning) {
      setColumnPinning(tableQueryState.columnPinning);
    }
  }, [tableQueryState?.columnPinning]);

  useEffect(() => {
    if (tableQueryState?.columnGrouping) {
      setColumnGrouping(tableQueryState.columnGrouping);
    }
  }, [tableQueryState?.columnGrouping]);

  useEffect(() => {
    if (tableQueryState?.columnAggregation) {
      setColumnAggregation(tableQueryState.columnAggregation);
    }
  }, [tableQueryState?.columnAggregation]);

  useEffect(() => {
    setPaginator({
      pageIndex: page - 1,
      pageSize: pageSize ?? 10,
    });
  }, [page, pageSize]);

  useEffect(() => {
    if (data.length > 0 && tableSelectedRows.length !== 0) {
      const currentPageRowIds = data.map(({ id, identifier }) => getSelectRowId({ id, identifier }, isHeliosTable));
      const areAllCurrentRowsSelected = currentPageRowIds.every((id) => tableSelectedRows.includes(id));

      selectAllSetValue('selectAll', areAllCurrentRowsSelected);
    }
  }, [data.length, selectAllSetValue, tableSelectedRows, data, isHeliosTable]);

  if (Object.keys(dataSchema).length === 0) {
    return null;
  }

  const hasInnerTable = !!innerTable;

  const rowsCount = tableInstance?.getRowCount();
  const tableRows = tableInstance.getRowModel().rows;
  const visibleTableRows = visibleRowCount ? tableRows.slice(0, visibleRowCount) : tableRows;

  return (
    <LoadingContainer loading={Boolean(isLoading)} showContent>
      <DndContext collisionDetection={closestCenter} modifiers={[restrictToVerticalAxis]} onDragEnd={handleDragEnd} sensors={sensors}>
        <FormProvider {...form}>
          <TableManagementProvider
            isHeliosTable={isHeliosTable}
            rows={tableInstance.getRowModel().rows}
            dataSchema={dataSchema}
            rowControls={rowControls}
            tableControls={tableControls}
            heliosRowFunctionsControls={heliosRowFunctionsControls}
            heliosRowControls={heliosRowControls}
            editRowId={editRowId}
            hasInnerTable={hasInnerTable}
            draggable={draggable}
            disableRowCancelEdit={disableRowCancelEdit}
            disableRowSave={disableRowSave}
            isEditableColumnsAllowed={isEditableColumnsAllowed}>
            <div>
              <Wrapper
                key={'container'}
                mobileApp={mobileApp}
                tableHeight={tableHeight}
                fixedHeight={Boolean(fixedHeight)}
                isHeliosTable={isHeliosTable}>
                {controls && (
                  <TableControl isPinnedDashboard={isPinnedDashboard} mobileApp={mobileApp}>
                    <Box sx={{ flex: 1, minWidth: 0 }}>{controls}</Box>
                    <ControlButtonsWrapper>
                      {!mobileApp && isEnableColumnHiding && !isOverview && (
                        <FeatureGate flag="F20-2718">
                          <ColumnVisibilityController
                            tableInstance={tableInstance}
                            columnVisibility={columnVisibility}
                            testIdPrefix={resolvedTestIdPrefix}
                            onAllColumnSizingReset={isColumnSizingResetEnabled ? handleAllColumnSizingReset : undefined}
                            hasChangedColumnSizing={hasChangedColumnSizing}
                          />
                        </FeatureGate>
                      )}
                      {!mobileApp && isEnableColumnGrouping && !isOverview && (
                        <FeatureGate flag="F20-2719">
                          <ColumnGroupingControler
                            tableInstance={tableInstance}
                            columnGrouping={columnGrouping}
                            testIdPrefix={resolvedTestIdPrefix}
                          />
                        </FeatureGate>
                      )}
                      {isEnableExportToCSV && !mobileApp && (
                        <Button
                          size="small"
                          sx={{ minWidth: 'fit-content' }}
                          onClick={handleExportToCSV}
                          variant="outlined"
                          disabled={!tableInstance.getRowModel().rows.length || tableSelectedRows.length > 0}
                          type="button"
                          startIcon={<FileDownload />}>
                          {t('table.buttons.exportCsv')}
                        </Button>
                      )}
                    </ControlButtonsWrapper>
                  </TableControl>
                )}
                {mobileApp ? (
                  isHeliosTable ? (
                    <MobileHeliosTable
                      editRowId={idsForMobileInRowEdit}
                      selectAllForm={selectAllForm}
                      rows={visibleTableRows}
                      dataSchema={dataSchema}
                      hideCheckbox={hideCheckbox}
                      selectedRows={tableSelectedRows}
                      onRowClick={handleEntireRowClick}
                      handleRowSelect={handleRowSelect}
                      heliosRowControls={heliosRowControls}
                      heliosRowFunctionsControls={heliosRowFunctionsControls}
                      onSaveEditedRow={onSaveEditedRow}
                      onCancelEditRow={onCancelEditRow}
                    />
                  ) : (
                    <MobileTable
                      agendaId={agendaId}
                      selectAllForm={selectAllForm}
                      rows={visibleTableRows}
                      dataSchema={dataSchema}
                      hideCheckbox={hideCheckbox}
                      selectedRows={tableSelectedRows}
                      onRowClick={handleEntireRowClick}
                      handleRowSelect={handleRowSelect}
                      onSaveEditedRow={onSaveEditedRow}
                      onCancelEditRow={onCancelEditRow}
                    />
                  )
                ) : viewType === VIEW_TYPE.CATALOGUE && viewWidgetId && !isModal ? (
                  <CatalogueWidget
                    rows={tableInstance.getRowModel().rows}
                    dataSchema={dataSchema}
                    viewWidgetId={viewWidgetId}
                    viewId={viewId}
                    dataD={dataD}
                    setCatalogData={setCatalogData}
                    visibilityResults={visibilityResults}
                    selectedRows={tableSelectedRows}
                    handleRowSelect={handleRowSelect}
                    onEntireRowClick={handleEntireRowClick}
                  />
                ) : (
                  <div className="table-overflow">
                    <StyledTable
                      style={{
                        ...columnSizeVars,
                        width: '100%',
                        minWidth: 'calc(var(--total-data-width) * 1px)',
                        tableLayout: 'fixed',
                      }}>
                      {!hideHeader && (
                        <TableHeader<T, P>
                          testIdPrefix={resolvedTestIdPrefix}
                          selectAllForm={selectAllForm}
                          tableInstance={tableInstance}
                          sort={sort}
                          onSort={onSort}
                          onSelectAll={handleToggleSelectAll}
                          unableSortFor={unableSortFor}
                          onAddButtonClick={onAddButtonClick}
                          tableControls={tableControls}
                          hideCheckbox={hideCheckbox}
                          isMultiSelect={isMultiSelect}
                          columnSizing={tableInstance.getState().columnSizing}
                          columnPinning={tableInstance.getState().columnPinning}
                          columnVisibility={tableInstance.getState().columnVisibility}
                          columnAggregation={resolvedColumnAggregation}
                          onColumnAggregationChange={handleColumnAggregationChange}
                          onColumnSizingReset={handleColumnSizingReset}
                          isColumnSizingResetEnabled={isColumnSizingResetEnabled}
                          isEnableColumnAggregation={isEnableColumnAggregation}
                        />
                      )}

                      {dataD.length ? (
                        <>
                          <TableBody<T, P>
                            testIdPrefix={resolvedTestIdPrefix}
                            selectAllForm={selectAllForm}
                            agendaId={agendaId}
                            dataSchema={dataSchema}
                            tableInstance={tableInstance}
                            selectedRows={tableSelectedRows}
                            handleRowSelect={handleRowSelect}
                            data={dataD}
                            visibleRowCount={visibleRowCount}
                            cellClicked={() => {}}
                            entireRowClicked={handleEntireRowClick}
                            showRowClickStyles={showRowClickStyles}
                            editRowId={editRowId}
                            innerTable={innerTable}
                            tableIndex={tableIndex}
                            rowControls={rowControls}
                            heliosRowControls={heliosRowControls}
                            heliosRowFunctionsControls={heliosRowFunctionsControls}
                            tableControls={tableControls}
                            clickableCellsKeys={clickableCellsKeys}
                            hideCheckbox={hideCheckbox}
                            onSaveEditedRow={onSaveEditedRow}
                            onCancelEditRow={onCancelEditRow}
                            isHeliosTable={isHeliosTable}
                            onBlurCapture={onBlurCapture}
                            columnVisibility={tableInstance.getState().columnVisibility}
                            columnAggregation={resolvedColumnAggregation}
                            visibilityResults={visibilityResults}
                          />
                          {hasAggregationFooter && (
                            <TableFooter<T, P>
                              tableInstance={tableInstance}
                              hideCheckbox={hideCheckbox}
                              columnAggregation={resolvedColumnAggregation}
                            />
                          )}
                        </>
                      ) : null}
                    </StyledTable>
                  </div>
                )}

                {!dataD.length && (isLoading ? <TableLoadingSkeleton /> : <NoDataWrapper>{noData ?? t('common.noData')}</NoDataWrapper>)}
              </Wrapper>

              {!hidePagination && (
                <Paginator
                  testIdPrefix={resolvedTestIdPrefix}
                  showAllRows={showAllRows || isEnableExportToCSV}
                  setPaginator={handlePaginatorChange}
                  rowsCount={rowsCount}
                  paginator={paginator}
                  zIndex={paginationZIndex}
                  disablePageSizeChange={disablePageSizeChange}
                />
              )}
              {mobileApp && <MobileSelectionCountFab count={tableSelectedRows.length} />}
            </div>
          </TableManagementProvider>
        </FormProvider>
      </DndContext>
    </LoadingContainer>
  );
};

export const Table = memo(TableBase) as typeof TableBase;
